/**
 * Contact/Lead Service
 * Handles contact form submissions and newsletter signups
 */

import apiClient, { ApiError } from "./client";
import { ENDPOINTS } from "./config";
import { getRecaptchaToken } from "./recaptcha";

// Contact form data interface
export interface ContactFormData {
  name: string;
  email: string;
  phone?: string;
  company?: string;
  subject: string;
  message: string;
  source?: string; // e.g., "website", "pricing_page"
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  referrer?: string;
}

// Newsletter signup data
export interface NewsletterData {
  email: string;
  source?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  referrer?: string;
}

// Response types
export interface ContactResponse {
  success: boolean;
  message: string;
  ticketId?: string;
}

export interface NewsletterResponse {
  success: boolean;
  message: string;
}

/**
 * Submit contact form
 */
export const submitContactForm = async (data: ContactFormData): Promise<ContactResponse> => {
  try {
    const recaptcha_token = await getRecaptchaToken('contact_submit');
    const response = await apiClient.post<ContactResponse>(ENDPOINTS.CONTACT.SUBMIT, {
      ...data,
      submitted_at: new Date().toISOString(),
      source: data.source || "marketing_website",
      recaptcha_token,
    });
    return response.data;
  } catch (error) {
    const apiError = error as ApiError;
    throw new Error(apiError.message || "Failed to submit contact form");
  }
};

/**
 * Subscribe to newsletter
 */
export const subscribeNewsletter = async (data: NewsletterData): Promise<NewsletterResponse> => {
  try {
    const recaptcha_token = await getRecaptchaToken('newsletter_subscribe');
    const response = await apiClient.post<NewsletterResponse>(ENDPOINTS.NEWSLETTER.SUBSCRIBE, {
      ...data,
      source: data.source || "marketing_website",
      recaptcha_token,
    });
    return response.data;
  } catch (error) {
    const apiError = error as ApiError;
    throw new Error(apiError.message || "Failed to subscribe to newsletter");
  }
};

const contactService = {
  submitContactForm,
  subscribeNewsletter,
};

export default contactService;
